import { assertUnreachableValue } from '#pplx/frontend/common/type_utils/unreachableValue.js';
import { SLASH_HINT_NODE_TYPE } from '#pplx/frontend/composer-sdk/lexical/nodes/SlashHintNode.js';
import { getMentionLinkMarkdown } from '#pplx/frontend/composer-sdk/lexical/utils/getMentionLinkMarkdown.js';
import { getSerializedTextContent, shouldSerializeLinkNodeAsPlainText, } from '#pplx/frontend/composer-sdk/serializedNodeText.js';
/**
 * Shares text output between the plain-text and Markdown entry points.
 */
export function serializeChildren({ children, context, separator = '', }) {
    const text = context?.text ?? children.map(getSerializedTextContent).join(separator);
    let startOffset = context?.startOffset ?? 0;
    return children
        .map((child, index) => {
        if (index > 0) {
            startOffset += separator.length;
        }
        const childText = getSerializedTextContent(child);
        const childContext = {
            endOffset: startOffset + childText.length,
            startOffset,
            text,
        };
        startOffset = childContext.endOffset;
        return serializeNode({ node: child, context: childContext });
    })
        .join(separator);
}
function serializeNode({ node, context, }) {
    switch (node.type) {
        case 'text':
            return node.text;
        case 'linebreak':
            return '\n';
        case 'link':
        case 'autolink': {
            const linkNode = node;
            const text = serializeChildren({ children: linkNode.children });
            if (shouldSerializeLinkNodeAsPlainText({ node: linkNode, context })) {
                return text;
            }
            return getMentionLinkMarkdown(text, linkNode.url, linkNode.title ?? undefined) ?? text;
        }
        case 'mention': {
            const { props } = node;
            switch (props.variant) {
                case 'tab':
                case 'user':
                    // User mentions keep the pplx://mention DSL in the query text so thread views render
                    // the person chip.
                    return getMentionLinkMarkdown(props.text, props.url) ?? props.text;
                case 'shortcut':
                case 'slash-command':
                    // Slash commands are re-parsed from the leading query text on the ASI ingress
                    // (parse_into_ask_params reads request.query_text); emit the literal `/<name>` token so
                    // the slash survives the markdown serializer that builds query_text.
                    return props.queryText ?? '';
                case 'skills':
                case 'plugin': {
                    const displayLabel = props.localizedName || props.text;
                    return getMentionLinkMarkdown(displayLabel, props.url) ?? displayLabel;
                }
                case 'sources':
                case 'space':
                case 'device':
                    return `@${props.text}`;
                case 'search-mode':
                    return '';
                default:
                    return assertUnreachableValue(props.variant);
            }
        }
        case SLASH_HINT_NODE_TYPE:
            return '';
        default:
            return 'children' in node
                ? serializeChildren({ children: node.children, context })
                : '';
    }
}
//# sourceMappingURL=serializeChildren.js.map